// src/services/api.js

import axios from 'axios';
// Import only the logger instance (configureLoggerEndpoint removed)
import { logger } from '../utils/logger'; 
import { resolveApiBaseUrl } from '../utils/apiConfig';

// --- Configuration ---

const resolvedEnvUrl = import.meta.env.VITE_API_URL?.trim();

const baseURL = resolveApiBaseUrl({
  envUrl: resolvedEnvUrl,
  isDev: import.meta.env.DEV
});

console.log(`[EduVibz API Config] Using API Base URL: ${baseURL}`);

// Removed logger configuration call from here

const api = axios.create({
  baseURL: baseURL, // Use the configured baseURL with /api prefix
  withCredentials: true, // Send cookies with requests (important for sessions)
});

// Check if essential Cognito env vars are present
const cognitoUserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || 'eu-north-1_DD8cWiI1e';
const cognitoClientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '4rnddm4bs9jrbq25qgup9jpki8';

// Log Cognito configuration for easier debugging in browser console
console.log('[EduVibz API Config] Cognito User Pool ID used:', cognitoUserPoolId || 'MISSING!');
console.log('[EduVibz API Config] Cognito Client ID used:', cognitoClientId || 'MISSING!');

if (!cognitoUserPoolId || !cognitoClientId) {
  const errorMsg = 'CRITICAL: Cognito User Pool ID or Client ID is missing in environment variables. Check Cloudflare Pages settings.';
  logger.error(errorMsg);
  console.error(errorMsg);
  // Optionally, you could throw an error here to halt execution if Cognito is essential immediately
  // throw new Error(errorMsg);
}

const loadCognitoService = () => import('./cognitoService');

const userPool = {
  getCurrentUser: () => null
};

const signUp = async (...args) => (await loadCognitoService()).signUp(...args);
const confirmSignUp = async (email, code) => (await loadCognitoService()).confirmSignUp(email, code, { api });
const resendConfirmationCode = async (...args) => (await loadCognitoService()).resendConfirmationCode(...args);
const signIn = async (...args) => (await loadCognitoService()).signIn(...args);
const forgotPassword = async (...args) => (await loadCognitoService()).forgotPassword(...args);
const confirmPassword = async (...args) => (await loadCognitoService()).confirmPassword(...args);
const getCurrentCognitoUser = async (...args) => (await loadCognitoService()).getCurrentCognitoUser(...args);

// --- Application Specific API Calls ---

const sendListeningEvent = (eventData) => {
  // Destructure to handle potential missing duration
  const { duration, currentTime, ...rest } = eventData;
  // Use currentTime as a fallback if duration is not a valid number
  const validDuration = (typeof duration === 'number' && !isNaN(duration)) ? duration : currentTime;

  return api.post('/listening-events', {
    ...rest,
    duration: validDuration,
    currentTime // Send currentTime regardless
  });
};

const logout = () => {
  loadCognitoService()
    .then(({ logoutCognitoUser }) => logoutCognitoUser())
    .catch((error) => logger.warn('Cognito sign out skipped', { error }));
  // Also clear local storage
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  logger.info('User logged out client-side.');

  // Optionally call backend logout endpoint if it does server-side session invalidation
  // return api.post('/auth/logout');
  return Promise.resolve(); // Return resolved promise after client logout
};

const notifySessionExpired = () => {
  localStorage.setItem('eduvibz.sessionExpired', 'true');
  window.dispatchEvent(new CustomEvent('eduvibz:session-expired'));
};

// --- Axios Interceptors ---

// Request interceptor to add Authorization header
api.interceptors.request.use(
  async (config) => {
    const method = (config.method || 'get').toLowerCase();
    const publicReadPaths = ['/curricula', '/grades', '/subjects', '/content-requests', '/contact', '/health', '/cookie-policy', '/public/top-songs'];
    const publicWritePaths = ['/contact'];
    const skipAuth = config.skipAuth === true;

    const isPublicPath = method === 'get'
      ? publicReadPaths.some(path => config.url.startsWith(path))
      : publicWritePaths.some(path => config.url.startsWith(path));

    if (!skipAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    if (isPublicPath && skipAuth) {
      logger.debug('Public API request sent without auth header', { path: config.url });
    }

    return Promise.resolve(config);
  },
  (error) => {
    logger.error('Axios request interceptor error', { error: error.message });
    return Promise.reject(error);
  }
);


// Response interceptor for handling token refresh on 401
// Be careful with this - ensure your backend actually requires refresh for 401s
// and that your refresh endpoint works correctly.
api.interceptors.response.use(
  (response) => response, // Simply return successful responses
  async (error) => {
    const originalRequest = error.config;

    // Check if it's a 401 error, not from a refresh attempt itself, and a refresh token exists
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh-token') &&
      localStorage.getItem('refreshToken')
    ) {
      originalRequest._retry = true; // Mark to prevent infinite loops
      const storedRefreshToken = localStorage.getItem('refreshToken');

      try {
        logger.info('Attempting token refresh due to 401');
        // Call the refreshToken function (defined below or imported)
        const { token } = await refreshToken(storedRefreshToken); // Assumes refresh returns { token }

        // Update local storage and default header for subsequent requests
        localStorage.setItem('token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Update the header of the original failed request
        originalRequest.headers['Authorization'] = `Bearer ${token}`;

        logger.info('Token refreshed successfully, retrying original request.');
        // Retry the original request with the new token
        return api(originalRequest);

      } catch (refreshError) {
        logger.error('Token refresh failed, logging out user.', { 
          error: refreshError.response?.data || refreshError.message || refreshError, 
          status: refreshError.response?.status 
        });
        // If refresh fails, logout the user (clear tokens, redirect to login)
        logout(); // Call your logout function
        notifySessionExpired();
        // Optionally redirect to login page
        // window.location.href = '/login'; 
        return Promise.reject(refreshError); // Reject with the refresh error
      }
    }
    
    // For errors other than 401 or if refresh shouldn't be attempted
    return Promise.reject(error);
  }
);

// --- API Function Definitions ---

// Music / Content
const fetchAlbums = () => api.get('/music/all');
const fetchAlbumById = (id) => api.get(`/music/${id}`);
const searchMusic = (params) => api.get('/music/search', { params });
const getRecentlyListened = () => api.get('/music/recently-listened');
const getPublicTopSongs = (limit = 3) => api.get('/public/top-songs', {
  params: { limit },
  skipAuth: true
});
const getPublicTopSongStream = (songId) => api.get(`/public/top-songs/${songId}/stream`, {
  skipAuth: true
});
const getChaptersBySubject = (subjectId) => {
  if (!subjectId) return Promise.reject(new Error('Subject ID is required'));
  return api.get(`/music/chapters-by-subject?subjectId=${subjectId}`);
};
const getChapterByNumber = (chapterNumber) => {
  if (!chapterNumber) return Promise.reject(new Error('Chapter number is required'));
  return api.get(`/music/chapters/${chapterNumber}`);
};
const getSongsByChapter = (chapterId) => {
  if (!chapterId) return Promise.reject(new Error('Chapter ID is required'));
  logger.info('Fetching songs for chapter', { chapterId });
  return api.get(`/music/songs-by-chapter/${chapterId}`);
};

// Curricula / Education Structure
const getCurricula = () => api.get('/curricula');
const getGrades = (curriculumId) => api.get(`/grades/${curriculumId}`);
const getSubjects = (gradeId) => api.get(`/subjects/${gradeId}`);

// User Data / Auth Backend Interaction (beyond Cognito direct calls)
const getUserData = () => api.get('/auth/me'); // Gets user data from *your* backend

// Contact Forms
const submitContactForm = (data) => api.post('/contact', { ...data, type: 'contact' });
const submitWaitlist = (data) => api.post('/contact', { ...data, type: 'waitlist' });
const submitFeatureRequest = (data) => api.post('/contact', { ...data, type: 'feature' });
const submitContribute = (data) => api.post('/contact', { ...data, type: 'contribute' });

// Playlists
const getPlaylists = () => api.get('/playlists');
const getPlaylist = (id) => api.get(`/playlists/${id}`); // Alias for getPlaylistById?
const createPlaylist = (playlistData) => api.post('/playlists', playlistData);
const updatePlaylist = (id, playlistData) => api.put(`/playlists/${id}`, playlistData);
const deletePlaylist = (id) => api.delete(`/playlists/${id}`);
const getPlaylistById = (playlistId) => api.get(`/playlists/${playlistId}`);
const addSongToPlaylist = (playlistId, songId) => api.post('/playlists/add-song', { playlistId, songId });
const removeSongFromPlaylist = (playlistId, songId) => api.post('/playlists/remove-song', { playlistId, songId });
const sharePlaylist = (playlistId, userEmail) => api.post('/playlists/share', { playlistId, userEmail });

// Challenges
const fetchUserChallenges = () => api.get('/challenges/user');
const startChallenge = (challengeId) => api.post(`/challenges/${challengeId}/start`);
const updateChallengeProgress = (challengeId, progress) => api.post('/challenges/progress', { challengeId, progress });
const getAllChallenges = (params) => api.get('/challenges', { params });
const getChallenge = (id) => api.get(`/challenges/${id}`);
const createChallenge = (challengeData) => api.post('/challenges', challengeData);
const updateChallenge = (id, challengeData) => api.put(`/challenges/${id}`, challengeData);
const deleteChallenge = (id) => api.delete(`/challenges/${id}`);

// Static Content
const getCookiePolicy = () => api.get('/cookie-policy');

// Cognito Password Management (User initiated)
const changePassword = async (...args) => (await loadCognitoService()).changePassword(...args);

// Re-exporting forgotPassword as requestPasswordReset for clarity if needed elsewhere
const requestPasswordReset = forgotPassword;

// Paystack Payment Functions (Interacting with your backend endpoints)
const initializePaystackPayment = async (email, planId) => {
  try {
    const response = await api.post('/paystack/initialize', { email, planId });
    return response.data;
  } catch (error) {
    logger.error('Payment initialization failed', { error: error.response?.data?.message || error.message });
    throw error; // Re-throw to be handled by calling component
  }
};

const verifyPaystackPayment = async (reference, planCode, isInitialCharge) => {
  try {
    // Ensure reference is included in the URL query parameter as expected by backend
    const response = await api.post(`/paystack/verify?reference=${encodeURIComponent(reference)}`, {
      planCode,
      isInitialCharge // Send these in the body
    });
    return response.data;
  } catch (error) {
    logger.error('Payment verification failed', { error: error.response?.data?.message || error.message });
    throw error;
  }
};

// Subscription Management
const getSubscriptionDetails = () => api.get('/subscriptions/details');

// Admin
const getAdminAnalytics = (params) => api.get('/admin/analytics', { params });
const getAdminUserAnalytics = (cognitoSub, params) => api.get(`/admin/analytics/users/${encodeURIComponent(cognitoSub)}`, { params });
const getAdminContentRequests = (params) => api.get('/admin/content-requests', { params });
const updateAdminContentRequest = (id, data) => api.put(`/admin/content-requests/${id}`, data);
const getAdminFeedbackSummaries = () => api.get('/admin/content-requests/feedback-summaries');
const createAdminRegenerationRequest = (data) => api.post('/admin/content-requests/regeneration', data);
const updateAdminFeedbackStatus = (feedbackId, data) => api.patch(`/admin/content-requests/feedback/${feedbackId}`, data);

// Community content requests
const getContentRequests = () => api.get('/content-requests');
const createContentRequest = (data) => api.post('/content-requests', data);
const voteContentRequest = (id) => api.post(`/content-requests/${id}/vote`);
const submitSongFeedback = (songId, data) => api.post(`/music/${songId}/feedback`, data);
const getNotifications = () => api.get('/notifications');
const markNotificationRead = (notificationId) => api.patch(`/notifications/${notificationId}/read`);
const markAllNotificationsRead = () => api.post('/notifications/read-all');


// --- Utility / Helper Functions ---

// Token Refresh Function (Called by interceptor)
const refreshToken = async (refreshTokenValue) => {
  if (!refreshTokenValue) {
     throw new Error("No refresh token available.");
  }
  try {
    // This endpoint needs to exist on your backend API
    const response = await api.post('/auth/refresh-token', { refreshToken: refreshTokenValue }); 
    if (!response.data || !response.data.token) {
        throw new Error("Invalid response from refresh token endpoint");
    }
    return response.data; // Expects backend to return { token: newIdToken, ... }
  } catch (error) {
    logger.error('API call to /auth/refresh-token failed', { 
       error: error.response?.data || error.message, 
       status: error.response?.status 
    });
    throw error; // Re-throw the error to be caught by the interceptor
  }
};

// Authentication Check (Simple check based on token presence)
const isAuthenticated = () => {
  // This is a basic check. A proper check might involve verifying token expiry
  // or making a quick call to a backend /auth/check endpoint.
  const token = localStorage.getItem('token'); 
  return !!token; 
};


// --- Exports ---

// Export the configured base URL for use in other modules like logger
export { baseURL }; 

// Export the configured axios instance
export default api;

// Export individual functions for use in components/stores
export {
  // Cognito specific exports (consider namespacing these?)
  userPool, // Legacy compatibility shim; Cognito itself is lazy-loaded by auth helpers.
  signUp,
  confirmSignUp,
  resendConfirmationCode,
  signIn,
  forgotPassword, // Keep original name if used directly
  confirmPassword, // Used for password reset flow
  changePassword, // User initiated password change when logged in
  requestPasswordReset, // Clearer name for forgot password flow initiation
  getCurrentCognitoUser as getCurrentUser, // Export Cognito user getter

  // App specific API calls
  sendListeningEvent,
  logout,
  fetchAlbums,
  fetchAlbumById,
  searchMusic,
  getCurricula,
  getGrades,
  getSubjects,
  getRecentlyListened,
  getPublicTopSongs,
  getPublicTopSongStream,
  getUserData, // Get data from *your* backend /auth/me
  submitContactForm,
  submitWaitlist,
  submitFeatureRequest,
  submitContribute,
  getPlaylists,
  getPlaylist,
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  getPlaylistById,
  addSongToPlaylist,
  removeSongFromPlaylist,
  sharePlaylist,
  fetchUserChallenges,
  startChallenge,
  updateChallengeProgress,
  getAllChallenges,
  getChallenge,
  createChallenge,
  updateChallenge,
  deleteChallenge,
  getChaptersBySubject,
  getChapterByNumber,
  getSongsByChapter,
  getCookiePolicy,
  initializePaystackPayment,
  verifyPaystackPayment,
  getSubscriptionDetails,
  getAdminAnalytics,
  getAdminUserAnalytics,
  getAdminContentRequests,
  updateAdminContentRequest,
  getAdminFeedbackSummaries,
  createAdminRegenerationRequest,
  updateAdminFeedbackStatus,
  getContentRequests,
  createContentRequest,
  voteContentRequest,
  submitSongFeedback,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,

  // Utilities / Helpers exported
  isAuthenticated,
  refreshToken, // Export refresh token function if needed elsewhere, otherwise keep internal

  // Export the instance if needed directly (rare)
  api as apiInstance 
};
